import { Controller } from "@hotwired/stimulus";
import { postMessageToPreviewIframes } from "../lib/preview_iframe_messaging";

const FLUSH_EVENT = "customization-draft:flush";
const FLUSHED_EVENT = "customization-draft:flushed";
const RESUME_EVENT = "customization-draft:resume";

// Wraps the customizer's settings form. Debounces input changes into a
// background fetch that writes to the server-side draft, processes the
// turbo-stream response, and warns the user before navigating away while
// changes are pending.
export default class extends Controller {
  connect() {
    this.timeout = null;
    this.pendingSave = null;
    this.dirtyFields = new Set();
    this.flushing = false;
    this.beforeUnloadHandler = this.handleBeforeUnload.bind(this);
    this.flushHandler = this.flush.bind(this);
    this.resumeHandler = this.resume.bind(this);

    window.addEventListener("beforeunload", this.beforeUnloadHandler);
    document.addEventListener(FLUSH_EVENT, this.flushHandler);
    document.addEventListener(RESUME_EVENT, this.resumeHandler);
  }

  disconnect() {
    if (this.timeout) clearTimeout(this.timeout);
    window.removeEventListener("beforeunload", this.beforeUnloadHandler);
    document.removeEventListener(FLUSH_EVENT, this.flushHandler);
    document.removeEventListener(RESUME_EVENT, this.resumeHandler);
  }

  save(event) {
    // Ignore file inputs (Uppy drives those via its own turbo-frame reload) and any input outside the event[] namespace.
    const target = event?.target;
    if (target?.type === "file") return;
    const name = target?.name;
    if (!name?.startsWith("event[")) return;
    this.dirtyFields.add(name);

    // postMessage updates the visible iframe instantly for templates
    // that use --custom-* CSS variables (e.g. the public landing page).
    // The autosave's iframe reload below covers templates that bake
    // colors into HTML at render time (e.g. email templates).
    this.optimisticPreviewUpdate(event);

    // Skip while flushing so a late keystroke can't race the publish/discard submit.
    if (this.flushing) return;
    if (this.timeout) clearTimeout(this.timeout);
    this.timeout = setTimeout(() => this.persist(), 200);
  }

  resume() {
    this.flushing = false;
  }

  // postMessage CSS-var change for an instant update. The autosave's
  // iframe reload still runs after, for email templates that bake
  // colors into HTML.
  optimisticPreviewUpdate(event) {
    const target = event?.target;
    if (!target?.name) return;
    const match = target.name.match(/^event\[(\w+)\]$/);
    if (!match) return;

    const fieldName = match[1];
    if (!fieldName.endsWith("_color")) return;
    if (!target.value) return;

    const message = {
      type: "customization-update",
      name: `--custom-${fieldName.replace(/_/g, "-")}`,
      value: target.value,
    };
    postMessageToPreviewIframes(message);
  }

  async flush() {
    // Cleared by RESUME_EVENT after submit-end.
    this.flushing = true;
    while (this.timeout || this.pendingSave) {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
        await this.persist();
      } else {
        await this.pendingSave;
      }
    }
    document.dispatchEvent(new CustomEvent(FLUSHED_EVENT));
  }

  // Serialize saves through pendingSave so an in-flight fetch's response
  // can't be raced by a newer one — without this, two autosaves firing
  // close together could land out of order and the older response's
  // renderStreamMessage would clobber the UI with stale draft state.
  async persist() {
    const url = this.element.action;
    if (!url) return;
    if (this.dirtyFields.size === 0) return;

    const sentFields = new Set(this.dirtyFields);
    this.dirtyFields.clear();
    const body = this.#buildBody(sentFields);

    const next = (this.pendingSave || Promise.resolve())
      .catch(() => {})
      .then(() => this.#runSave({ url, body, sentFields }));
    this.pendingSave = next;
    try {
      await next;
    } finally {
      // Only clear if no newer save has queued itself behind us.
      if (this.pendingSave === next) this.pendingSave = null;
    }
  }

  // Touched fields only, plus the framework hidden fields Rails needs.
  #buildBody(fieldNames) {
    const full = new FormData(this.element);
    const body = new FormData();
    for (const frameworkField of ["authenticity_token", "_method", "utf8"]) {
      const value = full.get(frameworkField);
      if (value !== null) body.append(frameworkField, value);
    }
    for (const name of fieldNames) {
      for (const value of full.getAll(name)) body.append(name, value);
    }
    return body;
  }

  #runSave({ url, body, sentFields }) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    return fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrf,
        Accept: "text/vnd.turbo-stream.html",
      },
      body,
    })
      .then(async (response) => {
        if (!response.ok) {
          // Requeue so the next save retries.
          for (const name of sentFields) this.dirtyFields.add(name);
          this.flashError(
            `Couldn't save your changes (HTTP ${response.status}).`,
          );
          return;
        }
        const html = await response.text();
        if (window.Turbo) window.Turbo.renderStreamMessage(html);
        if (!this.flushing) this.refreshPreviewIframes();
      })
      .catch((err) => {
        for (const name of sentFields) this.dirtyFields.add(name);
        console.error("[customization-draft] fetch failed", err);
        this.flashError("Couldn't save your changes. Check your connection.");
      });
  }

  // Append a hidden flash element for the global flash controller to
  // pick up and display via Toastify. Keep messages short.
  flashError(message) {
    const flashFrame = document.getElementById("flash");
    if (!flashFrame) return;
    const el = document.createElement("div");
    el.className = "hidden";
    el.dataset.controller = "flash";
    el.dataset.notice = "alert";
    el.textContent = message;
    flashFrame.appendChild(el);
  }

  // postMessage rather than bumping iframe.src so the iframe runs
  // Turbo.session.refresh() on itself — that engages turbo-refresh-method
  // morph (preserves scroll, focus, banner image) instead of a hard reload.
  refreshPreviewIframes() {
    postMessageToPreviewIframes({ type: "refresh-preview" });
  }

  handleBeforeUnload(event) {
    const banner = document.querySelector("unsaved-changes-banner");
    if (!banner || banner.dataset.hasDraft !== "true") return;
    event.preventDefault();
    event.returnValue = "";
  }
}
